import { ManipulatedVariable, StepTest, Disturbance, ProjectSummaryStats, CampaignSummaryStats } from '../types';
import { calculateReadinessScore, calculateDirectionalBalance } from './readinessCalculator';
import { generateXlsxBlob, generateMultiSheetXlsxBlob } from './xlsxService';

// Helper to escape CSV cell
function escapeCsv(val: any): string {
  if (val === null || val === undefined) return '""';
  const str = String(val).replace(/"/g, '""');
  return `"${str}"`;
}

// Download file utility in browser
export function downloadFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  downloadBlob(filename, blob);
}

// Download binary Blob utility
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const ExportService = {
  // ===== CSV EXPORTS =====
  exportMvsCsv(mvs: ManipulatedVariable[], campaignName: string, targetMovesPerMv: number = 20): void {
    const headers = [
      'Subcontroller',
      'Process Area',
      'Tag',
      'Description',
      'Units',
      'Recommended Step',
      'Approved Step',
      'Status',
      'Positive Moves (+)',
      'Negative Moves (-)',
      'Valid Moves',
      'Rejected Moves',
      'Readiness Score',
      'Directional Balance',
      'Notes',
    ];

    const rows = mvs.map((m) => {
      const mvTarget = m.targetMoves || targetMovesPerMv;
      const readiness = calculateReadinessScore(m.validMoves, mvTarget);
      const balance = calculateDirectionalBalance(m.positiveMoves, m.negativeMoves);
      return [
        m.controller,
        m.processArea || '-',
        m.tag,
        m.description,
        m.units,
        m.recommendedStepSize,
        m.approvedStepSize,
        m.status,
        m.positiveMoves,
        m.negativeMoves,
        m.validMoves,
        m.rejectedMoves,
        readiness,
        balance.ratioText,
        m.notes,
      ].map(escapeCsv).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\r\n');
    downloadFile(`APC_MV_Register_${campaignName.replace(/\s+/g, '_')}.csv`, csvContent, 'text/csv;charset=utf-8;');
  },

  exportStepTestsCsv(steps: StepTest[], campaignName: string): void {
    const headers = [
      'Date/Time',
      'MV Tag',
      'Direction',
      'Step Size',
      'Baseline SP',
      'Target SP',
      'Hold Duration (min)',
      'Steady State Achieved',
      'Disturbances Present',
      'Analyzer Response',
      'Quality Rating (1-5)',
      'Status',
      'Engineer',
      'Operator',
      'Comments',
    ];

    const rows = steps.map((s) => [
      s.timestamp,
      s.mvTag,
      s.direction,
      s.stepSize,
      s.baselineSp,
      s.targetSp,
      s.holdDuration,
      s.steadyStateAchieved || 'Pending Eval',
      s.disturbancesPresent || 'Pending Eval',
      s.analyzerResponseObserved || 'Pending Eval',
      s.dataQualityRating ? `${s.dataQualityRating}/5` : 'Pending Eval',
      s.status,
      s.engineer,
      s.operator,
      s.comments || '',
    ].map(escapeCsv).join(','));

    const csvContent = [headers.join(','), ...rows].join('\r\n');
    downloadFile(`APC_Step_Tests_${campaignName.replace(/\s+/g, '_')}.csv`, csvContent, 'text/csv;charset=utf-8;');
  },

  exportDisturbancesCsv(disturbances: Disturbance[], campaignName: string): void {
    const headers = [
      'Timestamp',
      'Category',
      'Controller',
      'Description',
      'Engineer',
    ];

    const rows = disturbances.map((d) => [
      d.timestamp,
      d.category,
      d.controller || 'Plant-Wide',
      d.description,
      d.engineer,
    ].map(escapeCsv).join(','));

    const csvContent = [headers.join(','), ...rows].join('\r\n');
    downloadFile(`APC_Disturbances_${campaignName.replace(/\s+/g, '_')}.csv`, csvContent, 'text/csv;charset=utf-8;');
  },

  // ===== TRUE EXCEL (.XLSX) MULTI-SHEET WORKBOOK EXPORT =====
  exportExcelWorkbook(
    projectName: string,
    mvs: ManipulatedVariable[],
    steps: StepTest[],
    disturbances: Disturbance[],
    stats?: ProjectSummaryStats,
    targetMovesPerMv: number = 20
  ): void {
    const target = targetMovesPerMv || 20;
    const adequateMin = Math.ceil(target * 0.5);
    const modelReadyCount = mvs.filter((m) => m.validMoves >= (m.targetMoves || target)).length;
    const adequateCount = mvs.filter((m) => {
      const mvT = m.targetMoves || target;
      const mvAdequateMin = Math.ceil(mvT * 0.5);
      return m.validMoves >= mvAdequateMin && m.validMoves < mvT;
    }).length;
    const insufficientCount = mvs.filter((m) => {
      const mvT = m.targetMoves || target;
      const mvAdequateMin = Math.ceil(mvT * 0.5);
      return m.validMoves < mvAdequateMin;
    }).length;
    const totalValidMoves = stats?.totalValidMoves ?? mvs.reduce((acc, m) => acc + (m.validMoves || 0), 0);
    const totalRejectedMoves = stats?.rejectedTestsCount ?? mvs.reduce((acc, m) => acc + (m.rejectedMoves || 0), 0);
    const targetMovesTotal = stats?.targetMovesTotal ?? (mvs.length * target);
    const progressPct = stats?.projectProgressPct ?? stats?.campaignProgressPct ?? (targetMovesTotal > 0 ? Math.min(100, Math.round((totalValidMoves / targetMovesTotal) * 100)) : 0);
    const reportStatus = progressPct >= 100
      ? 'Step Testing Complete (Model Ready)'
      : (stats?.activeTestsCount && stats.activeTestsCount > 0) || totalValidMoves > 0
      ? 'Step Testing In-Progress'
      : 'Step Testing Planned';

    // Sheet 1: Project Summary (Executive KPIs & Subcontroller Breakdown)
    const summaryRows: (string | number)[][] = [
      ['KEYERA ADVANCED PROCESS CONTROL - PROJECT EXECUTIVE SUMMARY', ''],
      ['Project Name', projectName],
      ['Project Status', reportStatus],
      ['Generated On', new Date().toISOString().replace('T', ' ').slice(0, 19)],
      ['Overall Progress', `${progressPct}%`],
      ['Total Manipulated Variables', mvs.length],
      [`Model Ready MVs (${target}+ moves)`, modelReadyCount],
      [`Adequate MVs (${adequateMin}-${target - 1} moves)`, adequateCount],
      [`Insufficient MVs (<${adequateMin} moves)`, insufficientCount],
      ['Total Valid Steps', totalValidMoves],
      ['Target Moves Total', targetMovesTotal],
      ['Remaining Moves', stats?.remainingMoves ?? Math.max(0, targetMovesTotal - totalValidMoves)],
      ['Total Rejected Moves', totalRejectedMoves],
      ['', ''],
      ['SUBCONTROLLER BREAKDOWN', ''],
      ['Subcontroller', 'Total MVs', 'Valid Moves', 'Target Moves', 'Readiness %', `Ready (${target}+)`, `Adequate (${adequateMin}-${target - 1})`]
    ];

    if (stats?.controllerStats && Object.keys(stats.controllerStats).length > 0) {
      for (const ctrl of Object.keys(stats.controllerStats)) {
        const data = stats.controllerStats[ctrl];
        if (data) {
          summaryRows.push([
            ctrl,
            data.totalMvs,
            data.validMoves,
            data.targetMoves,
            `${data.readinessPct}%`,
            data.mvsReadyCount,
            data.mvsAdequateCount
          ]);
        }
      }
    } else {
      const ctrlMap: Record<string, { total: number; valid: number; target: number; ready: number; adequate: number }> = {};
      for (const m of mvs) {
        const c = m.controller || 'Unassigned';
        const mvT = m.targetMoves || target;
        const mvAdequateMin = Math.ceil(mvT * 0.5);
        if (!ctrlMap[c]) ctrlMap[c] = { total: 0, valid: 0, target: 0, ready: 0, adequate: 0 };
        ctrlMap[c].total += 1;
        ctrlMap[c].valid += (m.validMoves || 0);
        ctrlMap[c].target += mvT;
        if (m.validMoves >= mvT) ctrlMap[c].ready += 1;
        else if (m.validMoves >= mvAdequateMin) ctrlMap[c].adequate += 1;
      }
      for (const [c, val] of Object.entries(ctrlMap)) {
        const pct = val.target > 0 ? Math.min(100, Math.round((val.valid / val.target) * 100)) : 0;
        summaryRows.push([c, val.total, val.valid, val.target, `${pct}%`, val.ready, val.adequate]);
      }
    }

    // Sheet 2: Step Tests Log
    const stepRows: (string | number)[][] = [
      ['Date/Time', 'MV Tag', 'Direction', 'Step Size', 'Baseline SP', 'Target SP', 'Hold Duration (min)', 'Status', 'Steady State Achieved', 'Disturbances Present', 'Quality Rating', 'Engineer', 'Operator', 'Comments']
    ];
    for (const s of steps) {
      stepRows.push([
        s.timestamp,
        s.mvTag,
        s.direction,
        s.stepSize,
        s.baselineSp,
        s.targetSp,
        s.holdDuration,
        s.status,
        s.steadyStateAchieved || 'Pending Eval',
        s.disturbancesPresent || 'Pending Eval',
        s.dataQualityRating ? `${s.dataQualityRating}/5` : '-',
        s.engineer,
        s.operator || '',
        s.comments || ''
      ]);
    }

    // Sheet 3: Model Readiness Register
    const readinessRows: (string | number)[][] = [
      ['Subcontroller', 'Process Area', 'Tag', 'Description', 'Units', 'Recommended Step', 'Approved Step', 'Valid Moves', 'Positive (+)', 'Negative (-)', 'Rejected', 'Balance Ratio', 'Readiness Score', 'Status', 'Notes']
    ];
    for (const m of mvs) {
      const mvTarget = m.targetMoves || target;
      const readiness = calculateReadinessScore(m.validMoves, mvTarget);
      const balance = calculateDirectionalBalance(m.positiveMoves, m.negativeMoves);
      readinessRows.push([
        m.controller,
        m.processArea || '-',
        m.tag,
        m.description,
        m.units,
        m.recommendedStepSize,
        m.approvedStepSize,
        m.validMoves,
        m.positiveMoves,
        m.negativeMoves,
        m.rejectedMoves,
        balance.ratioText || balance.skew,
        readiness,
        m.status,
        m.notes || ''
      ]);
    }

    // Sheet 4: Plant Disturbances Log
    const distRows: (string | number)[][] = [
      ['Timestamp', 'Category', 'Subcontroller', 'Description', 'Engineer']
    ];
    for (const d of disturbances) {
      distRows.push([
        d.timestamp,
        d.category,
        d.controller || 'Plant-Wide',
        d.description,
        d.engineer
      ]);
    }

    const blob = generateMultiSheetXlsxBlob([
      { name: 'Project Summary', rows: summaryRows },
      { name: 'Step Tests Log', rows: stepRows },
      { name: 'Model Readiness', rows: readinessRows },
      { name: 'Plant Disturbances', rows: distRows }
    ]);

    downloadBlob(`APC_All_Reports_${projectName.replace(/\s+/g, '_')}.xlsx`, blob);
  },

  exportProjectExcel(
    mvs: ManipulatedVariable[],
    steps: StepTest[],
    disturbances: Disturbance[],
    stats: ProjectSummaryStats,
    projectName: string
  ): void {
    this.exportExcelWorkbook(projectName, mvs, steps, disturbances, stats);
  },

  exportCampaignExcel(
    mvs: ManipulatedVariable[],
    steps: StepTest[],
    disturbances: Disturbance[],
    stats: CampaignSummaryStats,
    campaignName: string
  ): void {
    this.exportExcelWorkbook(campaignName, mvs, steps, disturbances, stats);
  },

  // ===== EXCEL & CSV IMPORT TEMPLATES =====
  downloadMvTemplateCsv(): void {
    const headers = [
      'Subcontroller',
      'Process Area',
      'Tag',
      'Description',
      'Units',
      'Recommended Step',
      'Approved Step',
      'Notes',
    ];

    const sampleRows = [
      ['Deethanizer', 'Feed System', 'FC-101', 'Deethanizer Total Feed Flow', 'm3/h', '5.0', '5.0', 'Primary feed throughput MV'],
      ['Deethanizer', 'Preheat Train', 'TC-102', 'Deethanizer Feed Preheat Temp', '°C', '2.5', '2.0', 'Hot/cold feed balance'],
      ['Frac 1', 'Feed System', 'FC-201', 'Frac 1 Raw Feed Charge Flow', 'm3/h', '8.0', '8.0', 'Frac 1 main feed'],
      ['Frac 1', 'Overhead & Reflux', 'FC-204', 'Frac 1 Reflux Flow Rate', 'm3/h', '6.0', '5.0', 'Propane HD-5 spec control'],
      ['Frac 2', 'Feed System', 'FC-301', 'Frac 2 Raw Feed Charge Flow', 'm3/h', '10.0', '8.0', 'Frac 2 main feed'],
      ['Frac 2', 'Overhead & Reflux', 'FC-304', 'Frac 2 Column Reflux Flow Rate', 'm3/h', '7.0', '6.0', 'Propane purity control'],
    ];

    const csv = [headers.join(','), ...sampleRows.map((r) => r.map(escapeCsv).join(','))].join('\r\n');
    downloadFile('APC_MV_Import_Template.csv', csv, 'text/csv;charset=utf-8;');
  },

  downloadMvTemplateExcel(): void {
    const rows: (string | number)[][] = [
      ['Subcontroller', 'Process Area', 'Tag', 'Description', 'Units', 'Recommended Step', 'Approved Step', 'Notes'],
      ['Deethanizer', 'Feed System', 'FC-101', 'Deethanizer Total Feed Flow', 'm3/h', 5.0, 5.0, 'Primary feed throughput MV'],
      ['Deethanizer', 'Preheat Train', 'TC-102', 'Deethanizer Feed Preheat Temp', '°C', 2.5, 2.0, 'Hot/cold feed balance'],
      ['Frac 1', 'Feed System', 'FC-201', 'Frac 1 Raw Feed Charge Flow', 'm3/h', 8.0, 8.0, 'Frac 1 main feed'],
      ['Frac 1', 'Overhead & Reflux', 'FC-204', 'Frac 1 Reflux Flow Rate', 'm3/h', 6.0, 5.0, 'Propane HD-5 spec control'],
      ['Frac 2', 'Feed System', 'FC-301', 'Frac 2 Raw Feed Charge Flow', 'm3/h', 10.0, 8.0, 'Frac 2 main feed'],
      ['Frac 2', 'Overhead & Reflux', 'FC-304', 'Frac 2 Column Reflux Flow Rate', 'm3/h', 7.0, 6.0, 'Propane purity control'],
    ];

    const blob = generateXlsxBlob('MV Import Template', rows);
    downloadBlob('APC_MV_Import_Template.xlsx', blob);
  },

  // ===== PDF / PRINT REPORT TRIGGER =====
  printReport(): void {
    window.print();
  },
};
